import axios from 'axios';
import { API_ROOT, logApiError } from '@/store/api';

// The change-email endpoint answers a refused address under `new_email` (a string
// from the shared validator, a list from the serializer) and everything else under
// `detail`. Fold that into one Error the dialog can route: `field` says whether the
// message belongs on the input or in the form-level banner.
const emailChangeError = e => {
    const data = e?.response?.data;
    const fieldMessage = Array.isArray(data?.new_email) ? data.new_email[0] : data?.new_email;
    const error = new Error(fieldMessage || data?.detail || 'Could not change the email. Please try again.');
    error.field = fieldMessage ? 'new_email' : null;
    return error;
};

// Deactivate and the two-factor reset (ENG-3439) answer a refusal with `detail`: a 403
// naming the rule (your own account, a staff account without superuser) or a 400 for an
// account the action doesn't apply to. The page shows it, so the Error carries it.
const accountActionError = (e, fallback) => new Error(e?.response?.data?.detail || fallback);

export default {
    namespaced: true,
    state: {
        headers: {},
    },
    mutations: {
        updateHeaders(state, headers) {
            state.headers = { ...headers };
        },
    },
    getters: {},
    actions: {
        async get709Response({ dispatch, rootGetters }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/admin/irs709_responses/${payload.user_id}/`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Resolves with the page, or `{ failed: true, status }` when the request failed
        // (`status` 0 when no response came back), so the Users page can tell a page that
        // no longer exists (404) from any other failure. Neither failure shape has
        // `results`, which is all SelectUserModal checks.
        async listUsers({ dispatch, rootGetters }, payloadStr = '') {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/accounts/${payloadStr !== '' ? `?${payloadStr}` : ''}`,
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return { failed: true, status: e?.response?.status ?? 0 };
            }
        },
        async impersonate({ commit, dispatch, rootGetters, rootState, state }, uid) {
            try {
                commit('updateHeaders', rootState.extraHeaders);
                const { data } = await axios.post(
                    `${API_ROOT}/impersonation/${uid}/start/`,
                    {},
                    rootGetters.axiosConfig,
                );
                const headers = {
                    ...state.headers,
                    ...data,
                };
                commit('updateExtraHeaders', headers, { root: true });
                const user = await axios.get(`${API_ROOT}/accounts/${uid}/`, rootGetters.axiosConfig);
                commit('updateImpersonatedUser', user.data, { root: true });
            } catch (e) {
                // Roll back before reporting failure. X-Impersonate is committed above
                // BEFORE the profile fetch, and both isImpersonating and axiosConfig key
                // off that header alone — so a failure in the fetch used to leave the
                // caller told "it failed" while every subsequent request still executed
                // as the client. Restoring state.headers (the snapshot taken on entry)
                // mirrors what stopImpersonate does, and is a no-op when the start call
                // itself was what failed.
                //
                // If the start POST succeeded, the server-side ImpersonationLog stays
                // open (no session_end); that is an audit artifact, not access, since
                // the backend reads impersonation from the header we just dropped.
                commit('updateExtraHeaders', state.headers, { root: true });
                commit('updateImpersonatedUser', null, { root: true });
                commit('updateHeaders', {});
                logApiError(e, dispatch);
                return false;
            }
        },
        // Takes `{ userId, skipOnboarding }` rather than a bare id: activation can also
        // clear the client's onboarding gate (onboarding_complete), and a Vuex action
        // gets a single payload.
        async activateUser({ dispatch, rootGetters }, { userId, skipOnboarding = false }) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/accounts/${userId}/activate/`,
                    { skip_onboarding: skipOnboarding },
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Both resolve with the refreshed row of the Users list, as activateUser does.
        async deactivateUser({ dispatch, rootGetters }, userId) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/accounts/${userId}/deactivate/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw accountActionError(e, 'Could not deactivate the user. Please try again.');
            }
        },
        async resetTwoFactor({ dispatch, rootGetters }, userId) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/accounts/${userId}/reset-two-factor/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw accountActionError(e, 'Could not reset two-factor authentication. Please try again.');
            }
        },
        async sendPasswordReset({ dispatch, rootGetters }, userId) {
            try {
                await axios.post(`${API_ROOT}/accounts/${userId}/send-password-reset/`, {}, rootGetters.axiosConfig);
                return true;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Staff-only (ENG-2948): sets the client's sign-in email directly, with no
        // verification code. Resolves with the refreshed ops row (the same shape the
        // Users list renders, so both surfaces merge it in place); rejects with an
        // Error carrying the server's message — unlike the boolean actions above,
        // because the reason an address was refused is what the dialog has to show.
        async changeUserEmail({ dispatch, rootGetters }, { userId, newEmail }) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/accounts/${userId}/email/`,
                    { new_email: newEmail },
                    rootGetters.axiosConfig,
                );
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw emailChangeError(e);
            }
        },
        async stopImpersonate({ commit, dispatch, rootGetters, rootState, state }) {
            try {
                const impersonate = JSON.parse(rootState.extraHeaders['X-Impersonate'] || '{}');
                // Impersonation routes are uuid-addressed; `uid` (integer pk) only
                // exists for the backend middleware's session-log check.
                const uid = impersonate.uuid || (rootState.impersonatedUser || {}).uuid;
                await axios.post(`${API_ROOT}/impersonation/${uid}/stop/`, {}, rootGetters.axiosConfig);
                commit('updateExtraHeaders', state.headers, { root: true });
                commit('updateImpersonatedUser', null, { root: true });
                commit('updateHeaders', {});
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
    },
};
